import * as React from 'react';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { Dayjs } from 'dayjs';
import { dayjs } from '../ex_dayjs';
import MDateControl from './MDateControl';

/** Any value accepted as a date input by this control. */
type MDatePickerValue = Dayjs | Date | string | number | null | undefined;

/**
 * Props for {@link MDatePicker}.
 */
interface MDatePickerProps extends Omit<React.ComponentProps<typeof MDateControl>, 'value' | 'defaultValue' | 'onChange'> {
    /** `dayjs` format string. Defaults to `'MM/DD/YYYY'`. */
    format?: string;
    /** Whether the reported value is converted to UTC. Defaults to `false`. */
    isUTC?: boolean;
    /** Field label. */
    label?: string;
    /** Controlled value. */
    value?: MDatePickerValue;
    /** Initial value applied whenever it changes. */
    defaultValue?: MDatePickerValue;
    /**
     * Called with the formatted date string produced by {@link MDateControl},
     * or `null` if cleared/invalid.
     */
    onChange: (formatted: string | null) => void;
}

/**
 * A single-date picker: wraps {@link MDateControl} in a `dayjs`
 * {@link LocalizationProvider}, converting the incoming `value`/`defaultValue`
 * to a `Dayjs` instance before handing off.
 */
const MDatePicker: React.FC<MDatePickerProps> = ({
    format = 'MM/DD/YYYY',
    isUTC = false,
    label, value, defaultValue, onChange, ...props
}) => {
    const [mValue, setMValue] = React.useState<Dayjs | null>(null);

    React.useEffect(() => {
        if (value) setMValue(dayjs(value));
        else setMValue(null);
    }, [value]);

    React.useEffect(() => {
        if (defaultValue) setMValue(dayjs(defaultValue));
    }, [defaultValue]);

    const handleChange = (newValue: string | null) => {
        onChange(newValue);
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MDateControl
                {...props}
                label={label}
                defaultValue={mValue}
                format={format}
                isUTC={isUTC}
                onChange={handleChange}
            />
        </LocalizationProvider>
    );
};

export default React.memo(MDatePicker);
